import React, { useState, useEffect } from 'react';
import { 
  Stepper, 
  Step, 
  StepLabel, 
  List, 
  ListItemText, 
  Checkbox,
  Typography, 
  Box, 
  CircularProgress,
  Alert
} from '@mui/material';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import api from '../../../services/api';
import toastUtils from '../../../utils/toastUtils';
import useSettings from '../../../hooks/useSettings';
import StandardModalAdv from '../../../components/shared/StandardModalAdv';

const steps = ['Verificar Configuração', 'Selecionar Agentes', 'Resultado da Importação'];

const ImportAssistantsModal = ({ open, onClose, onImportComplete }) => {
  const { getAll } = useSettings();
  
  const [activeStep, setActiveStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [openAIAssistants, setOpenAIAssistants] = useState([]);
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [importResults, setImportResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  
  // Verifica se temos chave global configurada
  const hasOpenAIKey = !!settings?.openAiKey;

    // CORREÇÃO: Carregar configurações quando o modal abrir
    useEffect(() => {
      if (open) {
        loadInitialData();
      }
    }, [open]);

  useEffect(() => {
    // Se o modal abrir e tivermos chave global, busca os assistentes automaticamente
    if (open && hasOpenAIKey && activeStep === 0 && openAIAssistants.length === 0) {
      handleFetchAssistants();
    }
  }, [open, activeStep, hasOpenAIKey]);

    const loadInitialData = async () => {
    try {      
      // Se não temos configurações, carregar
      if (!settings || Object.keys(settings).length === 0) {
        const data = await getAll();
        let newSettings = {};
        for (const { key, value } of data) {
          newSettings[key] = value;
        }
        
        setSettings(newSettings);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setOpenAIAssistants([]);
    setSelectedAssistants([]);
    setImportResults([]);
    setFetchError(null);
  };

  const handleFetchAssistants = async () => {
    setLoading(true);
    setFetchError(null);
    
    try {
      const response = await api.post('/assistants/fetch-openai');
      
      if (response.data && Array.isArray(response.data)) {
        setOpenAIAssistants(response.data);
        toastUtils.success(`${response.data.length} assistentes encontrados`, 'Busca Concluída');
        handleNext();
      } else {
        toastUtils.warning('Nenhum assistente encontrado', 'Busca Vazia');
      }
    } catch (error) {
      console.error('Erro ao buscar assistentes:', error);
      setFetchError(error.response?.data?.message || 'Erro ao buscar assistentes da OpenAI');
    } finally {
      setLoading(false);
    }
  };

  const handleImportAssistants = async () => {
    setLoading(true);
    try {
      const response = await api.post('/assistants/import', {
        assistantIds: selectedAssistants,
      });
      
      if (response.data && Array.isArray(response.data)) {
        setImportResults(response.data);
        toastUtils.success(`${response.data.length} assistentes importados com sucesso`, 'Importação Concluída');
      } else {
        toastUtils.warning('Nenhum assistente foi importado', 'Importação Vazia');
      }
      handleNext();
      onImportComplete();
    } catch (error) {
      console.error('Erro na importação:', error);
      toastUtils.error(error.response?.data?.message || 'Erro ao importar assistentes', 'Erro na Importação');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderStepContent = () => {
    // Se não tem chave, mostra apenas mensagem de erro
    if (!hasOpenAIKey) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Chave de API não encontrada
            </Typography>
            <Typography>
              Por favor, entre em contato com o Administrador da Empresa e solicite que seja feita a configuração.
            </Typography>
          </Alert>
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            {fetchError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Erro ao buscar assistentes
                </Typography>
                <Typography variant="body2">
                  {fetchError}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Buscando assistentes na OpenAI...
                </Typography>
                <Typography variant="body2">
                  Estamos buscando seus assistentes usando a chave configurada globalmente.
                </Typography>
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          </Box>
        );

      case 1:
        return openAIAssistants.length > 0 ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Selecione os assistentes para importar
            </Typography>
            
            <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '400px', mb: 2 }}>
              <List>
                {openAIAssistants.map((assistant, index) => (
                  <div style={{ display: 'flex', alignItems: 'center'}} key={index}>
                    <Checkbox
                      edge="start"
                      checked={selectedAssistants.includes(assistant.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAssistants([...selectedAssistants, assistant.id]);
                        } else {
                          setSelectedAssistants(selectedAssistants.filter(id => id !== assistant.id));
                        }
                      }}
                    />
                    <ListItemText 
                      primary={assistant.name} 
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          Modelo: {assistant.model}
                        </Typography>
                      }
                    />
                  </div>
                ))}
              </List>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="body2">
                {selectedAssistants.length} assistente(s) selecionado(s)
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Nenhum assistente encontrado
            </Typography>
          </Box>
        );

      case 2:
        return importResults.length > 0 ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Agentes importados com sucesso
            </Typography>
            
            <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '400px', mb: 2 }}>
              <List>
                {importResults.map((result, index) => (
                  <ListItemText 
                    primary={result.name} 
                    secondary={`ID: ${result.id}`}
                  />
                ))}
              </List>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Typography variant="body2" color="success.main">
                {importResults.length} assistente(s) importado(s) com sucesso
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Nenhum assistente foi importado
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const getModalActions = () => {
    const actions = [];

    // Botão Fechar sempre presente
    actions.push({
      label: 'Fechar',
      onClick: handleClose,
      variant: 'outlined',
      color: 'secondary'
    });

    // Fluxo quando não tem chave - apenas botão fechar
    if (!hasOpenAIKey) {
      return actions;
    }

    // Botão Voltar quando aplicável
    if (activeStep > 0 && activeStep < steps.length - 1) {
      actions.push({
        label: 'Voltar',
        onClick: handleBack,
        variant: 'outlined',
        color: 'primary',
        disabled: loading
      });
    }

    // Botões específicos por passo
    switch (activeStep) {
      case 0:
        if (fetchError) {
          actions.push({
            label: 'Tentar Novamente',
            onClick: handleFetchAssistants,
            variant: 'contained',
            color: 'primary'
          });
        }
        break;
      
      case 1:
        if (openAIAssistants.length > 0) {
          actions.push({
            label: loading ? 'Importando...' : 'Importar Selecionados',
            onClick: handleImportAssistants,
            variant: 'contained',
            color: 'primary',
            disabled: selectedAssistants.length === 0 || loading,
            loading: loading
          });
        } else {
          actions.push({
            label: 'Tentar Novamente',
            onClick: () => {
              handleReset();
              handleFetchAssistants();
            },
            variant: 'contained',
            color: 'primary'
          });
        }
        break;
      
      case 2:
        if (importResults.length === 0) {
          actions.push({
            label: 'Tentar Novamente',
            onClick: () => {
              handleReset();
              handleFetchAssistants();
            },
            variant: 'contained',
            color: 'primary'
          });
        }
        break;
    }

    return actions;
  };

  const getSubtitle = () => {
    if (hasOpenAIKey) {
      return 'Usando configuração global da empresa';
    }
    return null;
  };

  return (
    <StandardModalAdv
      open={open}
      onClose={handleClose}
      title="Importar Agentes da OpenAI"
      subtitle={getSubtitle()}
      icon={CloudDownloadIcon}
      maxWidth="md"
      allowFullscreen
      showCloseButton
      fullWidth
      actions={getModalActions()}
      loading={loading}
    >
      {/* Não mostrar stepper se não tiver chave */}
      {hasOpenAIKey && (
        <Box sx={{ mb: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
      
      <Box sx={{ minHeight: '300px' }}>
        {renderStepContent()}
      </Box>
    </StandardModalAdv>
  );
};

export default ImportAssistantsModal;